import { isSupabaseConfigured, supabase } from "../lib/supabase";

const STORAGE_KEY = "almoune_customers";

export const defaultCustomers = [
  {
    id: "cust-demo-1",
    firstName: "Nadine",
    lastName: "Khoury",
    phone: "06 12 34 56 78",
    email: "nadine.khoury@email.com",
    address: "15 Rue des Lilas, 91000 Evry",
    postcode: "91000",
    city: "Evry",
    notes: "Cliente reguliere, preference pour les produits bio.",
    totalOrders: 5,
    totalSpent: 87.50,
    lastVisit: "2026-07-20T14:30:00Z",
    createdAt: "2026-01-15T10:00:00Z",
  },
  {
    id: "cust-demo-2",
    firstName: "Karim",
    lastName: "Hanna",
    phone: "07 98 76 54 32",
    email: "karim.hanna@email.com",
    address: "8 Avenue de la Republique, 91000 Evry",
    postcode: "91000",
    city: "Evry",
    notes: "",
    totalOrders: 3,
    totalSpent: 42.00,
    lastVisit: "2026-07-18T09:15:00Z",
    createdAt: "2026-03-01T10:00:00Z",
  },
];

const normalizeCustomer = (c) => ({
  id: c.id || `cust-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  title: c.title || c.civility || "",
  type: c.type || "client",
  firstName: c.firstName || "",
  lastName: c.lastName || "",
  company: c.company || "",
  phone: c.phone || "",
  email: c.email || "",
  address: c.address || "",
  postcode: c.postcode || "",
  city: c.city || "",
  billingAddress: c.billing_address || c.billingAddress || "",
  billingPostcode: c.billing_postcode || c.billingPostcode || "",
  billingCity: c.billing_city || c.billingCity || "",
  notes: c.notes || "",
  active: c.active === undefined ? true : Boolean(c.active),
  newsletter: Boolean(c.newsletter),
  newsletterOptin: Boolean(c.newsletterOptin || c.optin || c.newsletter_optin),
  totalOrders: Number(c.totalOrders || 0),
  totalSpent: Number(c.totalSpent || 0),
  lastVisit: c.lastVisit || null,
  createdAt: c.createdAt || new Date().toISOString(),
});

const readCache = () => {
  if (typeof window === "undefined") return [...defaultCustomers];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [...defaultCustomers];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.length) return [...defaultCustomers];
    return parsed.map(normalizeCustomer);
  } catch {
    return [...defaultCustomers];
  }
};

const writeCache = (customers) => {
  const normalized = customers.map(normalizeCustomer);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  }
  return normalized;
};

export const getCustomers = () => readCache();

export const loadCustomers = async () => {
  const cached = readCache();
  if (!isSupabaseConfigured || !supabase) return cached;
  try {
    const { data, error } = await supabase
      .from("customers")
      .select("*")
      .order("last_name", { ascending: true });
    if (error || !data) return cached;
    return writeCache(data.map((row) => ({
      id: row.id,
      title: row.title || "",
      type: row.type || "client",
      firstName: row.first_name || "",
      lastName: row.last_name || "",
      company: row.company || "",
      phone: row.phone || "",
      email: row.email || "",
      address: row.address || "",
      postcode: row.postcode || "",
      city: row.city || "",
      notes: row.notes || "",
      active: row.active === undefined ? true : Boolean(row.active),
      newsletter: Boolean(row.newsletter),
      newsletterOptin: Boolean(row.newsletter_optin || row.optin),
      totalOrders: row.total_orders || 0,
      totalSpent: row.total_spent || 0,
      lastVisit: row.last_visit || null,
      createdAt: row.created_at || new Date().toISOString(),
    })));
  } catch {
    return cached;
  }
};

export const saveCustomer = async (customer) => {
  const normalized = normalizeCustomer(customer);
  const all = readCache();
  const idx = all.findIndex((c) => c.id === normalized.id);
  const updated = idx >= 0 ? all.map((c, i) => (i === idx ? normalized : c)) : [...all, normalized];
  const saved = writeCache(updated);
  if (!isSupabaseConfigured || !supabase) return saved;
  try {
    await supabase.from("customers").upsert({
      id: normalized.id,
      title: normalized.title,
      type: normalized.type,
      first_name: normalized.firstName,
      last_name: normalized.lastName,
      company: normalized.company,
      phone: normalized.phone,
      email: normalized.email,
      address: normalized.address,
      postcode: normalized.postcode,
      city: normalized.city,
      billing_address: normalized.billingAddress,
      billing_postcode: normalized.billingPostcode,
      billing_city: normalized.billingCity,
      notes: normalized.notes,
      active: normalized.active,
      newsletter: normalized.newsletter,
      newsletter_optin: normalized.newsletterOptin,
      total_orders: normalized.totalOrders,
      total_spent: normalized.totalSpent,
      last_visit: normalized.lastVisit,
      created_at: normalized.createdAt,
    }, { onConflict: "id" });
  } catch {
    // cache already written
  }
  return saved;
};

export const deleteCustomer = async (id) => {
  const all = readCache();
  const updated = all.filter((c) => c.id !== id);
  writeCache(updated);
  if (!isSupabaseConfigured || !supabase) return true;
  try {
    const { error } = await supabase.from("customers").delete().eq("id", id);
    if (error) throw error;
  } catch (err) {
    throw err;
  }
  return true;
};

/**
 * Find a customer by phone number.
 */
export const findCustomerByPhone = (phone) => {
  if (!phone) return null;
  const normalized = phone.replace(/[\s\-\.]/g, "").trim();
  if (!normalized) return null;
  const customers = readCache();
  return customers.find((c) => {
    const cPhone = (c.phone || "").replace(/[\s\-\.]/g, "").trim();
    return cPhone === normalized || cPhone.endsWith(normalized) || normalized.endsWith(cPhone);
  }) || null;
};

/**
 * Auto-create or update a customer from cashier order data.
 */
export const upsertCustomerFromOrder = async ({ clientName, clientPhone, orderAmount }) => {
  if (!clientName && !clientPhone) return null;
  const existing = clientPhone ? findCustomerByPhone(clientPhone) : null;
  if (existing) {
    const updated = {
      ...existing,
      totalOrders: existing.totalOrders + 1,
      totalSpent: Number((existing.totalSpent + Number(orderAmount || 0)).toFixed(2)),
      lastVisit: new Date().toISOString(),
      firstName: existing.firstName || (clientName || "").split(" ")[0] || "",
      lastName: existing.lastName || (clientName || "").split(" ").slice(1).join(" ") || "",
    };
    await saveCustomer(updated);
    return updated;
  }
  const parts = (clientName || "").trim().split(" ");
  const newCustomer = {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" ") || "",
    phone: clientPhone || "",
    totalOrders: 1,
    totalSpent: Number(Number(orderAmount || 0).toFixed(2)),
    lastVisit: new Date().toISOString(),
  };
  const saved = await saveCustomer(newCustomer);
  return saved[saved.length - 1];
};
